import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import type { TimelineEventProps, TimelineMarkerColor } from './TimelineEvent';

export interface TimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Timeline content — intended to be one or more `TimelineEvent` children,
   * each typically composing a `TimelineEventDescription`. Typed as
   * `React.ReactNode` to allow common conditional rendering (`cond && <…/>`);
   * non-element children are passed through untouched by the color cascade.
   */
  children?: React.ReactNode;
  /**
   * Design-token color cascaded to the circle marker of every TimelineEvent
   * child that has not set its own `color`. When unset, child colors are left
   * untouched.
   */
  color?: TimelineMarkerColor;
}

/**
 * Timeline component visualizes a chronological sequence of events.
 * Built entirely from EDU atomic components (Heading, Text, Icon) and design tokens.
 * Expects TimelineEvent children, with TimelineEventDescription nested inside each event.
 */
export const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
  ({ className, color, children, ...props }, ref) => {
    const themedChildren =
      color === undefined
        ? children
        : React.Children.map(children, (child) => {
            if (!React.isValidElement<TimelineEventProps>(child)) {
              return child;
            }
            return React.cloneElement(child, {
              color: child.props.color ?? color,
            });
          });

    return (
      <div
        ref={ref}
        className={cn('relative flex flex-col gap-xs', className)}
        aria-label='Timeline of events'
        {...props}
      >
        {themedChildren}
      </div>
    );
  },
);

Timeline.displayName = 'Timeline';
